import React, { useEffect, useMemo, useState } from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Snackbar,
  Button,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography
} from '@mui/material';
import TranslateIcon from '@mui/icons-material/Translate';
import CheckIcon from '@mui/icons-material/Check';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

export default function LanguageMenu({ compact }){
  const { i18n, t } = useTranslation();
  const languagedata = useSelector(state => state.languagedata);
  const [anchorEl, setAnchorEl] = useState(null);
  const [showUpdateSnackbar, setShowUpdateSnackbar] = useState(false);
  const [selectedLangName, setSelectedLangName] = useState('');
  const open = Boolean(anchorEl);
  const list = useMemo(
    () => (languagedata && languagedata.langlist ? languagedata.langlist : {}),
    [languagedata]
  );

  const currentKey = (() => {
    const keys = Object.keys(list || {});
    for (const k of keys){
      if (list[k].langLocale === i18n.language) return k;
    }
    return null;
  })();

  useEffect(() => {
    // Initialize from localStorage if present
    try {
      const stored = localStorage.getItem('lang');
      if (stored && stored !== i18n.language) {
        // Find the language in list and load its keyValuePairs
        const keys = Object.keys(list || {});
        for (const k of keys) {
          if (list[k].langLocale === stored && list[k].keyValuePairs) {
            i18n.addResourceBundle(stored, 'translations', list[k].keyValuePairs, true, true);
            const localeAlias = stored === 'nb' ? 'no' : stored === 'no' ? 'nb' : null;
            if (localeAlias) {
              i18n.addResourceBundle(localeAlias, 'translations', list[k].keyValuePairs, true, true);
            }
            break;
          }
        }
        i18n.changeLanguage(stored);
      }
    } catch(e) {}
  }, [i18n, list]);

  const handleSelect = (key) => {
    try {
      const lang = list[key];
      if (lang?.keyValuePairs) {
        i18n.addResourceBundle(lang.langLocale, 'translations', lang.keyValuePairs, true, true);
        const localeAlias =
          lang.langLocale === 'nb' ? 'no' : lang.langLocale === 'no' ? 'nb' : null;
        if (localeAlias) {
          i18n.addResourceBundle(localeAlias, 'translations', lang.keyValuePairs, true, true);
        }
      }
      i18n.changeLanguage(lang.langLocale);
      moment.locale(lang.dateLocale);
      localStorage.setItem('lang', lang.langLocale);
      setSelectedLangName(lang.langName);
      setShowUpdateSnackbar(true);
    } catch(e) {}
    setAnchorEl(null);
  };

  const handleRefresh = () => {
    setShowUpdateSnackbar(false);
    window.location.reload();
  };

  return (
    <>
      <Tooltip title={t('language') || 'Language'}>
        <IconButton
          size={compact ? 'small' : 'medium'}
          color="inherit"
          onClick={(e)=>setAnchorEl(e.currentTarget)}
          aria-haspopup="true"
          aria-controls={open? 'lang-menu': undefined}
          aria-expanded={open? 'true': undefined}
        >
          <TranslateIcon />
        </IconButton>
      </Tooltip>
      <Menu
        id="lang-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={()=>setAnchorEl(null)}
        keepMounted
        PaperProps={{
          sx: { minWidth: 180, borderRadius: 2 }
        }}
      >
        <Typography sx={{ px: 2, py: 1, fontSize: 12, color: '#6B7280', fontWeight: 500 }}>
          {t('select_language') || 'Select Language'}
        </Typography>
        <Divider sx={{ mb: 0.5 }} />
        {Object.keys(list).map((key) => (
          <MenuItem
            key={key}
            selected={currentKey===key}
            onClick={()=>handleSelect(key)}
            sx={{ py: 1.5 }}
          >
            <ListItemText primary={list[key].langName} />
            {currentKey === key && (
              <ListItemIcon sx={{ minWidth: 'auto', ml: 2 }}>
                <CheckIcon sx={{ fontSize: 18, color: '#16A34A' }} />
              </ListItemIcon>
            )}
          </MenuItem>
        ))}
      </Menu>

      {/* Language Update Confirmation Snackbar */}
      <Snackbar
        open={showUpdateSnackbar}
        autoHideDuration={6000}
        onClose={() => setShowUpdateSnackbar(false)}
        message={`${t('language_updated') || 'Language updated to'} ${selectedLangName}`}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        action={
          <Button
            color="primary"
            size="small"
            onClick={handleRefresh}
            startIcon={<RefreshIcon />}
            sx={{ color: '#fff', fontWeight: 600 }}
          >
            {t('apply_language') || 'Refresh'}
          </Button>
        }
        sx={{
          '& .MuiSnackbarContent-root': {
            bgcolor: '#111827',
            borderRadius: 2,
          }
        }}
      />
    </>
  );
}
